import React, { useState, useRef, useCallback } from 'react';
import { NodeViewWrapper } from '@tiptap/react';
import { Editor } from '@tiptap/react';

interface FigureComponentProps {
    node: any;
    updateAttributes: (attributes: Record<string, any>) => void;
    selected: boolean;
    getPos: () => number | undefined;
    editor: Editor;
}

const FigureComponent: React.FC<FigureComponentProps> = ({
    node,
    updateAttributes,
    selected,
    getPos,
    editor,
}) => {
    const [isEditingCaption, setIsEditingCaption] = useState(false);
    const [captionValue, setCaptionValue] = useState(node.attrs.caption || '');
    const [imageError, setImageError] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const captionTextareaRef = useRef<HTMLTextAreaElement>(null);

    // Safe update function that validates position before updating
    const safeUpdateAttributes = useCallback((attrs: Record<string, any>) => {
        try {
            // Check if editor is still valid
            if (!editor || editor.isDestroyed) {
                console.warn('[FigureComponent] Editor is destroyed, cannot update attributes');
                return false;
            }

            // Check if position is valid
            const pos = getPos();
            if (pos === undefined || pos < 0) {
                console.warn('[FigureComponent] Invalid position, cannot update attributes');
                return false;
            }

            // Verify position is within document bounds
            const docSize = editor.state.doc.content.size;
            if (pos >= docSize) {
                console.warn('[FigureComponent] Position outside document bounds:', pos, 'doc size:', docSize);
                return false;
            }

            // Use requestAnimationFrame to ensure DOM is stable before updating
            requestAnimationFrame(() => {
                try {
                    // Re-check position validity after animation frame
                    if (!editor || editor.isDestroyed) return;
                    
                    const currentPos = getPos();
                    if (currentPos === undefined || currentPos < 0) return;
                    
                    const currentDocSize = editor.state.doc.content.size;
                    if (currentPos >= currentDocSize) return;

                    updateAttributes(attrs);
                } catch (error) {
                    console.error('[FigureComponent] Error in deferred updateAttributes:', error);
                }
            });
            
            return true;
        } catch (error) {
            console.error('[FigureComponent] Error updating attributes:', error);
            return false;
        }
    }, [editor, getPos, updateAttributes]);

    const handleCaptionSave = () => {
        safeUpdateAttributes({ caption: captionValue });
        setIsEditingCaption(false);
    };

    const handleCaptionCancel = () => {
        setCaptionValue(node.attrs.caption || '');
        setIsEditingCaption(false);
    };

    // Auto-resize textarea based on content
    const adjustTextareaHeight = useCallback(() => {
        const textarea = captionTextareaRef.current;
        if (textarea) {
            // Reset height to auto to get the correct scrollHeight
            textarea.style.height = 'auto';
            // Set height to scrollHeight (content height)
            textarea.style.height = `${textarea.scrollHeight}px`;
        }
    }, []);

    // Adjust textarea height when caption value changes or when editing starts
    React.useEffect(() => {
        if (isEditingCaption) {
            // Use a small delay to ensure the textarea is rendered
            setTimeout(() => {
                adjustTextareaHeight();
            }, 0);
        }
    }, [captionValue, isEditingCaption, adjustTextareaHeight]);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        // Allow Enter for new lines, but save on Ctrl+Enter or Cmd+Enter
        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            handleCaptionSave();
        } else if (e.key === 'Escape') {
            e.preventDefault();
            handleCaptionCancel();
        }
        // Let Enter create new lines naturally
    };

    const handleImageError = () => {
        // Image failed to load - show placeholder instead
        setImageError(true);
    };

    // Reset error state if src changes
    React.useEffect(() => {
        setImageError(false);
    }, [node.attrs.src]);

    // Sync caption value when node.attrs.caption changes (e.g., after undo/redo)
    React.useEffect(() => {
        if (!isEditingCaption && node.attrs.caption !== captionValue) {
            setCaptionValue(node.attrs.caption || '');
        }
    }, [node.attrs.caption, isEditingCaption, captionValue]);

    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file && file.type.startsWith('image/')) {
            const reader = new FileReader();
            reader.onload = (e) => {
                const src = e.target?.result as string;
                if (src) {
                    safeUpdateAttributes({
                        src,
                        alt: file.name,
                    });
                }
            };
            reader.readAsDataURL(file);
        }
        // Reset the input
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const hasNoImage = !node.attrs.src || node.attrs.src === null || node.attrs.src === '';

    return (
        <NodeViewWrapper className={`figure-wrapper ${selected ? 'selected' : ''}`}>
            <figure className="tiptap-figure">
                {hasNoImage ? (
                    <div className="tiptap-figure-placeholder" style={{
                        padding: '40px',
                        textAlign: 'center',
                        color: '#999',
                        backgroundColor: '#f5f5f5',
                        border: '2px dashed #ccc',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '12px',
                        minHeight: '150px'
                    }}
                    onClick={handleUploadClick}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#f0f0f0';
                        e.currentTarget.style.borderColor = '#999';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#f5f5f5';
                        e.currentTarget.style.borderColor = '#ccc';
                    }}
                    title="Click to upload image">
                        <div style={{ fontSize: '48px' }}>🖼</div>
                        <div style={{ fontSize: '14px', fontWeight: '500' }}>Click to upload image</div>
                        <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleFileUpload}
                            accept="image/*"
                            style={{ display: 'none' }}
                        />
                    </div>
                ) : imageError ? (
                    <div className="tiptap-figure-error" style={{
                        padding: '40px',
                        textAlign: 'center',
                        color: '#999',
                        fontStyle: 'italic',
                        backgroundColor: '#f5f5f5',
                        border: '1px dashed #ddd',
                        borderRadius: '4px'
                    }}>
                        {node.attrs.alt || 'Image failed to load'}
                    </div>
                ) : (
                    <img
                        src={node.attrs.src}
                        alt={node.attrs.alt || ''}
                        className="tiptap-figure-image"
                        onError={handleImageError}
                    />
                )}
                <figcaption className="tiptap-figure-caption">
                    {isEditingCaption ? (
                        <div className="caption-edit-container">
                            <textarea
                                ref={captionTextareaRef}
                                value={captionValue}
                                onChange={(e) => {
                                    setCaptionValue(e.target.value);
                                    // Adjust height after value change
                                    setTimeout(() => adjustTextareaHeight(), 0);
                                }}
                                onKeyDown={handleKeyDown}
                                onBlur={handleCaptionSave}
                                placeholder="Enter image caption... (Ctrl+Enter or Cmd+Enter to save)"
                                className="caption-input"
                                autoFocus
                                rows={1}
                                style={{
                                    resize: 'none',
                                    overflow: 'hidden',
                                    minHeight: '1.5em',
                                    lineHeight: '1.5',
                                    width: '100%',
                                    padding: '4px 8px',
                                    border: '1px solid #ccc',
                                    borderRadius: '4px',
                                    fontFamily: 'inherit',
                                    fontSize: 'inherit',
                                }}
                            />
                        </div>
                    ) : (
                        <div
                            className="caption-display"
                            onClick={() => {
                                setIsEditingCaption(true);
                                // Adjust height after focusing
                                setTimeout(() => adjustTextareaHeight(), 0);
                            }}
                            title="Click to edit caption"
                        >
                            {node.attrs.caption || 'Click to add caption...'}
                        </div>
                    )}
                </figcaption>
            </figure>
        </NodeViewWrapper>
    );
};

export default FigureComponent; 